// Item 98 — FilterPanel data layer: batched, debounced, cached facet counts from the governed
// server-side engine. The panel never loads raw rows, so RLS/CLS and row limits are untouched.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { toActiveFilters } from "@/components/bi/FilterBar";

export const BLANK = "(Blank)";
export const PANEL_TYPES = ["checkbox", "search", "toggle", "tier", "numrange", "daterange", "multiselect", "select"];

export const NUM_RANGE_PRESETS = [
  { key: "lt1k", label: "Under 1,000", min: null, max: 1000 },
  { key: "1k5k", label: "1,000 – 5,000", min: 1000, max: 5000 },
  { key: "5k25k", label: "5,000 – 25,000", min: 5000, max: 25000 },
  { key: "25k100k", label: "25,000 – 100,000", min: 25000, max: 100000 },
  { key: "gt100k", label: "Over 100,000", min: 100000, max: null },
];

export const TIER_LEVELS = [
  { key: "4plus", label: "4 & above", op: "gte", value: 4 },
  { key: "3plus", label: "3 & above", op: "gte", value: 3 },
  { key: "2plus", label: "2 & above", op: "gte", value: 2 },
  { key: "1plus", label: "1 & above", op: "gte", value: 1 },
];

export const inPanel = (f) => f.in_panel !== false && PANEL_TYPES.includes(f.type);

const stripped = (a) => {
  const { dataset_id, __scope, ...rest } = a; // eslint-disable-line no-unused-vars
  return rest;
};

/** Build one facets request per dataset used by the panel's definitions. */
export function buildFacetRequests(defs, values) {
  const active = toActiveFilters(defs, values);
  const byDs = {};
  const ensure = (ds) => {
    if (!byDs[ds]) byDs[ds] = { dataset_id: ds, fields: [], numeric: [], toggles: [], tiers: [], filters: [] };
    return byDs[ds];
  };
  defs.forEach((f) => {
    if (!f.dataset_id) return;
    const r = ensure(f.dataset_id);
    if (f.type === "checkbox" || f.type === "multiselect" || f.type === "select") {
      if (!r.fields.some((x) => x.field === f.field)) {
        r.fields.push({ field: f.field, limit: Math.min(Number(f.max_options) || 300, 500), sort: f.sort === "alpha" ? "alpha" : "count" });
      }
    } else if (f.type === "numrange" && (f.ranges || []).length) {
      r.numeric.push({ field: f.field, ranges: f.ranges });
    } else if (f.type === "tier" && (f.levels || []).length) {
      r.tiers.push({ field: f.field, levels: f.levels.map((l) => ({ key: l.key, op: l.op || "gte", value: l.value })) });
    } else if (f.type === "toggle") {
      r.toggles.push({ key: f.id, filter: { field: f.field, op: f.op || "eq", value: f.value ?? true } });
    }
  });
  active.forEach((a) => {
    if (!a.dataset_id || !byDs[a.dataset_id]) return;
    byDs[a.dataset_id].filters.push(stripped(a));
  });
  return Object.values(byDs);
}

const cache = new Map();
const CACHE_MAX = 60;

/** Debounced facet fetching for every dataset the panel touches. */
export function useFacets({ defs, values, publicToken = null, enabled = true, debounce = 220 }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);
  const reqs = useMemo(() => (enabled ? buildFacetRequests(defs, values) : []), [defs, values, enabled]);
  const key = JSON.stringify(reqs);

  useEffect(() => {
    if (!enabled || !reqs.length) { setData({}); return undefined; }
    const mine = ++seq.current;
    const cached = cache.get(key);
    if (cached) { setData(cached); return undefined; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const results = await Promise.all(reqs.map((r) => (publicToken
          ? api.post("/public/facets", { ...r, token: publicToken })
          : api.post("/facets", r)).then(({ data: d }) => [r.dataset_id, d]).catch(() => [r.dataset_id, null])));
        if (mine !== seq.current) return;
        const next = {};
        results.forEach(([ds, d]) => { if (d) next[ds] = d; });
        if (cache.size > CACHE_MAX) cache.clear();
        cache.set(key, next);
        setData(next);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, debounce);
    return () => clearTimeout(timer.current);
    // reqs is tracked via its serialised key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, publicToken, enabled, debounce]);

  const optionsFor = useCallback((f) => {
    const d = data[f.dataset_id];
    const entry = d?.fields?.[f.field];
    return entry?.options || [];
  }, [data]);

  const bucketsFor = useCallback((f) => {
    const d = data[f.dataset_id];
    const o = {};
    (d?.numeric?.[f.field]?.buckets || []).forEach((b) => { o[b.key] = b.count; });
    return o;
  }, [data]);

  const statsFor = useCallback((f) => data[f.dataset_id]?.numeric?.[f.field]?.stats || {}, [data]);
  const tiersFor = useCallback((f) => data[f.dataset_id]?.tiers?.[f.field] || {}, [data]);
  const toggleCount = useCallback((f) => data[f.dataset_id]?.toggles?.[f.id], [data]);
  const totals = useMemo(() => {
    const out = {};
    Object.entries(data).forEach(([ds, d]) => { out[ds] = { count: d.total, grand_total: d.grand_total }; });
    return out;
  }, [data]);
  const primaryTotal = useMemo(() => {
    const first = Object.values(totals)[0];
    return first || null;
  }, [totals]);

  return { data, loading, optionsFor, bucketsFor, statsFor, tiersFor, toggleCount, totals, primaryTotal };
}

/** Chips describing everything currently applied, each removable. */
export function activeChips(defs, values) {
  const chips = [];
  defs.forEach((f) => {
    const v = values[f.id];
    if (v == null || v === "" || (Array.isArray(v) && !v.length)) return;
    const label = f.label || f.field;
    let text = "";
    let exclude = false;
    if (f.type === "checkbox" || f.type === "multiselect") {
      const arr = Array.isArray(v) ? v : v.v || [];
      if (!arr.length) return;
      exclude = !Array.isArray(v) && !!v.exclude;
      text = arr.length > 2 ? `${arr.slice(0, 2).join(", ")} +${arr.length - 2}` : arr.join(", ");
    } else if (f.type === "numrange") {
      if (Array.isArray(v)) text = `${v[0] ?? "≤"} – ${v[1] ?? "+"}`;
      else text = ((f.ranges || []).find((r) => String(r.key) === String(v.preset)) || {}).label || "range";
    } else if (f.type === "tier") {
      text = ((f.levels || []).find((l) => String(l.key) === String(v)) || {}).label || String(v);
    } else if (f.type === "toggle") {
      if (!v) return;
      text = "on";
    } else if (f.type === "daterange") {
      if (Array.isArray(v)) text = `${v[0] || "…"} → ${v[1] || "…"}`;
      else if (typeof v === "object") text = `last ${v.n} days`;
      else text = String(v).replace(/_/g, " ");
    } else text = Array.isArray(v) ? v.join(", ") : String(v);
    chips.push({ id: f.id, label, text, exclude });
  });
  return chips;
}

export async function downloadFilteredCsv({ datasetId, defs, values, columns = [], sort = [], publicToken = null, filename = "filtered.csv" }) {
  const filters = toActiveFilters(defs, values).filter((a) => a.dataset_id === datasetId).map(stripped);
  const body = { dataset_id: datasetId, filters, columns, sort };
  const url = publicToken ? "/public/facets/rows.csv" : "/facets/rows.csv";
  const { data } = await api.post(url, publicToken ? { ...body, token: publicToken } : body, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}
